/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import { ARCHE_TYPES } from "../data";
import { Archetype, ArchetypeId } from "../types";
import { useLanguage } from "../context/LanguageContext";
import { Sparkles, Compass, ShieldAlert, Heart, Scroll, Sword, BookOpen } from "lucide-react";

interface ArchetypeSelectorProps {
  onArchetypeSelect: (archetypeId: ArchetypeId) => void;
  selectedArchetypeId: ArchetypeId | null;
}

export default function ArchetypeSelector({ onArchetypeSelect, selectedArchetypeId }: ArchetypeSelectorProps) {
  const { isRtl, t } = useLanguage();
  
  // Set default active archetype to the first one or pre-selected one
  const [activeArchetype, setActiveArchetype] = useState<Archetype>(
    ARCHE_TYPES.find(a => a.id === selectedArchetypeId) || 
    ARCHE_TYPES.find(a => a.id === "self") || 
    ARCHE_TYPES[0]
  );

  // Sync state if selectedArchetypeId changes externally (like from quiz results)
  useEffect(() => {
    if (selectedArchetypeId) {
      const match = ARCHE_TYPES.find(a => a.id === selectedArchetypeId);
      if (match) {
        setActiveArchetype(match);
      }
    }
  }, [selectedArchetypeId]);

  const handleArchetypeClick = (archetype: Archetype) => {
    setActiveArchetype(archetype);
    onArchetypeSelect(archetype.id);
  };

  // Helper to render appropriate icon for archetypes matching the screenshot
  const getArchetypeIcon = (id: string, sizeClass = "w-6 h-6") => {
    switch (id) {
      case "self":
        return <Sparkles className={`${sizeClass} text-amber-400`} />;
      case "shadow":
        return <ShieldAlert className={`${sizeClass} text-rose-500`} />;
      case "persona":
        return <Compass className={`${sizeClass} text-blue-400`} />;
      case "anima_animus":
        return <Heart className={`${sizeClass} text-pink-500`} />;
      case "wise_old_man":
        return <Scroll className={`${sizeClass} text-emerald-400`} />;
      case "hero":
        return <Sword className={`${sizeClass} text-sky-400`} />;
      default:
        return <Sparkles className={`${sizeClass} text-amber-400`} />;
    }
  };

  const handleScrollToArticles = () => {
    const target = document.getElementById("inscriptions");
    if (target) {
      target.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section className="bg-[#0b0b0b] px-4 py-12 md:py-16 relative border-b border-[#d4af37]/10" id="archetypes-section">
      {/* Subtle background glow */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(212,175,55,0.01),transparent_70%)] pointer-events-none" />

      <div className="max-w-3xl mx-auto">
        
        {/* EXACT TITLE FROM SCREENSHOT */}
        <div className="text-center mb-8">
          <h2 className="text-base md:text-lg font-extrabold tracking-wider text-[#d4af37] font-sans">
            {isRtl ? "— — نقشه کهن‌الگوهای روان — —" : "— — Map of Psychical Archetypes — —"}
          </h2>
          <p className="text-[11px] text-gray-400 font-sans mt-1">
            {isRtl 
              ? "بر روی کتیبه هر یک از کهن‌الگوهای روان کلیک کنید تا راز اسطوره‌ای آن نمایان شود."
              : "Click on any archetype tablet to decode its mythic secrets."
            }
          </p>
        </div>

        {/* 2-COLUMN GRID (Exactly like the screenshot, super compact, symmetrical 3x2) */}
        <div className="grid grid-cols-2 gap-4 max-w-2xl mx-auto mb-8">
          {ARCHE_TYPES.map((arch) => {
            const isActive = activeArchetype.id === arch.id;
            return (
              <button
                key={arch.id}
                id={`compact-grid-btn-${arch.id}`}
                onClick={() => handleArchetypeClick(arch)}
                className={`relative flex flex-col items-center justify-center p-5 rounded-2xl border transition-all duration-300 min-h-[140px] text-center cursor-pointer select-none overflow-hidden ${
                  isActive
                    ? "bg-[#181410] border-[#d4af37] shadow-[0_0_20px_rgba(212,175,55,0.22)] scale-[1.02] z-10"
                    : "bg-[#121212]/80 border-white/5 hover:border-white/10 hover:bg-[#151515]"
                }`}
              >
                {/* Top: Icon */}
                <div className="mb-3">
                  {getArchetypeIcon(arch.id, "w-6 h-6")}
                </div>

                {/* Middle: Persian Name */}
                <h3 className={`text-base font-black tracking-tight mb-0.5 ${
                  isActive ? "text-[#ebd3a8]" : "text-gray-300"
                }`}>
                  {isRtl ? arch.name : arch.englishName}
                </h3>

                {/* Bottom: English Name */}
                <span className="text-xs text-gray-400 font-medium group-hover:text-gray-300 transition-colors">
                  {isRtl ? arch.englishName : arch.name}
                </span>

                {/* Symmetrical active underline bar */}
                {isActive && (
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-500 via-[#d4af37] to-amber-500 rounded-b-2xl" />
                )}
              </button>
            );
          })}
        </div>

        {/* REVELATION SCROLL (Matches screenshot styling exactly) */}
        <div id="archetype-revelation" className="scroll-mt-24">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeArchetype.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.25 }}
              className="relative bg-gradient-to-b from-[#141210] to-[#0c0a09] border border-[#d4af37]/20 rounded-2xl p-6 md:p-8 shadow-[0_15px_40px_rgba(0,0,0,0.6)]"
            >
              {/* Top-Right Tag as shown in screenshot */}
              <div className="absolute top-5 right-5 md:right-8">
                <span className="text-[10px] font-mono font-bold text-[#d4af37] uppercase tracking-wider bg-amber-500/10 px-3 py-1 rounded border border-[#d4af37]/20">
                  {activeArchetype.englishName}
                </span>
              </div>

              {/* Archetype Main Title */}
              <div className="text-start mt-4 mb-5 pb-4 border-b border-[#d4af37]/10">
                <h3 className="text-xl md:text-2xl font-black text-gray-200">
                  {isRtl 
                    ? <>کهن‌الگوی <span className="text-[#ebd3a8]">{activeArchetype.name}</span></>
                    : <><span className="text-[#ebd3a8]">{activeArchetype.englishName}</span> Archetype</>
                  }
                </h3>
                
                {/* Epic adaptation exactly like screenshot (Red label) */}
                <div className="mt-3 text-sm">
                  <span className="text-rose-500 font-extrabold ml-1 font-sans">
                    {t("epicAdaptation")}:
                  </span>
                  <span className="text-[#ebd3a8]/90 font-bold font-sans">
                    {isRtl ? activeArchetype.persianEquivalent : (activeArchetype.persianEquivalentEn || activeArchetype.persianEquivalent)}
                  </span>
                </div>
              </div>

              {/* Detail Content */}
              <div className="space-y-4 text-start">
                
                {/* Description */}
                <p className="text-gray-300 text-xs md:text-sm leading-relaxed text-justify font-sans">
                  {isRtl ? activeArchetype.description : (activeArchetype.descriptionEn || activeArchetype.description)}
                </p>

                {/* Symbol & Jungian commentary details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                  <div className="bg-black/40 rounded-xl p-3.5 border border-[#d4af37]/10">
                    <span className="text-[9px] text-gray-400 uppercase tracking-widest font-mono block mb-1">
                      {t("shahnamehSymbol")}
                    </span>
                    <span className="text-xs font-bold text-[#ebd3a8]">
                      {isRtl ? activeArchetype.symbol : (activeArchetype.symbolEn || activeArchetype.symbol)}
                    </span>
                  </div>

                  <div className="bg-[#171310] border-r-3 border-[#d4af37] rounded-l-md p-3.5">
                    <span className="text-[9px] text-gray-400 font-bold block mb-1">
                      {t("jungianPerspective")}
                    </span>
                    <p className="text-[11px] text-gray-300 italic leading-relaxed">
                      {isRtl ? activeArchetype.jungianConcept : (activeArchetype.jungianConceptEn || activeArchetype.jungianConcept)}
                    </p>
                  </div>
                </div>

                {/* Supplementary Article link */}
                <div className="flex justify-end pt-2 border-t border-[#d4af37]/5">
                  <button
                    onClick={handleScrollToArticles}
                    className="text-[11px] font-bold text-amber-400/80 hover:text-amber-300 transition-colors flex items-center gap-1.5 cursor-pointer"
                  >
                    <BookOpen className="w-3.5 h-3.5" />
                    <span>{t("discoverArticles")}</span>
                  </button>
                </div>

              </div>

            </motion.div>
          </AnimatePresence>
        </div>

      </div>
    </section>
  );
}
